/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject } from 'react';
import * as React from 'react';

import { HoverEffectWrapper } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';
import { FormattedNumber } from 'js/lib/coursera.react-intl';

import { Button, Typography2 } from '@coursera/cds-core';
import type { ProductType, SectionName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { ProgramAdmissionDeadlines } from 'bundles/degree-description/types';
import { PRODUCT_VARIANT_TO_PRODUCT_NAME } from 'bundles/premium-hub/constants';
import { useModuleSectionEventTrackingData } from 'bundles/premium-hub/hooks/useModuleSectionEventTrackingData';
import type { ProductCardProps } from 'bundles/premium-hub/types';
import { getUrlFromProductVariant } from 'bundles/premium-hub/utils';
import {
  getEventingProductTypeFromVariant,
  removeProductIdPrefix,
} from 'bundles/premium-hub/utils/dataTransformationUtils';
import { getDeadlineCopyForDegreeCard } from 'bundles/unified-common/utils/getDeadlineCopyForDegreeCard';
import { getDeadlineCopyForHubPage } from 'bundles/unified-common/utils/getDeadlineCopyForHubPage';

import _t from 'i18n!nls/premium-hub';

type Props = {
  product: ProductCardProps;
  entityIndex: number;
  sectionName: SectionName;
  onClick?: () => void;
  subindex?: number;
};

const styles = {
  card: css`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-200);

    &:has(a:focus-visible) {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
      border-radius: var(--cds-border-radius-200);
    }
  `,
  contentTop: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
  `,
  partnerRow: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  partnerLogo: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-50);

    /* In dark mode, we want the background color to be white */
    /* stylelint-disable-next-line color-no-hex, function-no-unknown */
    background-color: light-dark(var(--cds-color-neutral-background-primary), #fff);
  `,
  description: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-150) var(--cds-spacing-200);
  `,
  factRow: css`
    padding: var(--cds-spacing-150) var(--cds-spacing-150);
  `,
  factRowShaded: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
  `,
  ctaRow: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
    margin-top: auto;
  `,
  stretchedLink: css`
    /* CDS Button sets position: relative on its root for its own internal use, which would
       otherwise become the containing block for ::before below, shrinking it back down to
       just the button instead of stretching over the whole (position: relative) card. */
    position: static !important;

    &::before {
      content: '';
      cursor: inherit;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,
};

const DegreeSpecCard = ({ product, entityIndex, sectionName, onClick, subindex }: Props) => {
  const trackV3 = useTracker();
  const { index: sectionIndex } = useModuleSectionEventTrackingData();
  const { name, productVariant, slug, partner, description, programDeadlines } = product;

  const ariaDeadlineCopy = getDeadlineCopyForHubPage(
    (programDeadlines ?? []) as ProgramAdmissionDeadlines,
    getDeadlineCopyForDegreeCard
  ).body;

  const deadlineDate = getDeadlineCopyForHubPage(
    (programDeadlines ?? []) as ProgramAdmissionDeadlines,
    (_deadlineType, date) => ({ body: date ?? '' })
  ).body;

  const productCardTrackingDataV3 = {
    productCard: {
      index: entityIndex,
    },
    product: {
      id: removeProductIdPrefix(product.id ?? ''),
      slug: product.slug ?? '',
      name: product.name,
      type: getEventingProductTypeFromVariant(product.productVariant) as ProductType,
    },
    pageSection: { sectionName, index: sectionIndex, ...(subindex !== undefined && { subindex }) },
  };

  const degreeCardRef: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    productCardTrackingDataV3,
    [],
    { fullyVisible: true }
  );

  if (!name || !slug) {
    return null;
  }

  const url = getUrlFromProductVariant(productVariant, slug);
  const productName = PRODUCT_VARIANT_TO_PRODUCT_NAME[productVariant];

  const trackClick = () => {
    if (onClick) onClick();
    trackV3('click_product_card', productCardTrackingDataV3);
  };

  const ariaLabel = partner?.name
    ? _t('Learn more about #{productName}, offered by #{partnerName}. #{description}. #{deadlineCopy}', {
        productName: name,
        partnerName: partner.name,
        description: description ?? '',
        deadlineCopy: ariaDeadlineCopy,
      })
    : _t('Learn more about #{productName}. #{description}. #{deadlineCopy}', {
        productName: name,
        description: description ?? '',
        deadlineCopy: ariaDeadlineCopy,
      });

  return (
    <HoverEffectWrapper css={styles.card} data-testid="top-degree-card-redesign" ref={degreeCardRef}>
      <div css={styles.contentTop}>
        <div css={styles.partnerRow}>
          {partner && partner.squareLogo ? (
            <Imgix
              alt={_t('#{partnerName} logo', { partnerName: partner.name })}
              src={partner.squareLogo}
              imgParams={{ auto: 'format,compress', fit: 'fill', width: 56, height: 56 }}
              css={styles.partnerLogo}
            />
          ) : null}
          <Typography2 component="span" variant="bodyTertiary" color="supportText">
            {partner?.name ?? _t('Coursera Project Network')}
          </Typography2>
        </div>
        <Typography2 component="h3" variant="subtitleMedium">
          {productName ? _t('#{name} #{productName}', { name, productName }) : name}
        </Typography2>
      </div>
      {description && (
        <Typography2 component="p" variant="bodySecondary" css={styles.description}>
          {description}
        </Typography2>
      )}
      {product.degreeAttributes?.tuitionInUsd != null && (
        <Typography2 component="p" variant="bodySecondary" css={[styles.factRow, styles.factRowShaded]}>
          <strong>{_t('Tuition: ')}</strong>
          <FormattedNumber
            value={product.degreeAttributes.tuitionInUsd}
            currency="USD"
            style="currency"
            maximumFractionDigits={0}
            minimumFractionDigits={0}
          />
        </Typography2>
      )}
      {product.duration && (
        <Typography2 component="p" variant="bodySecondary" css={styles.factRow}>
          <strong>{_t('Duration: ')}</strong>
          {product.duration}
        </Typography2>
      )}
      {deadlineDate && (
        <Typography2 component="p" variant="bodySecondary" css={[styles.factRow, styles.factRowShaded]}>
          <strong>{_t('Application deadline: ')}</strong>
          {deadlineDate}
        </Typography2>
      )}
      <div css={styles.ctaRow}>
        <Button
          variant="primary"
          size="small"
          fullWidth
          component="a"
          href={url}
          onClick={trackClick}
          aria-label={ariaLabel}
          css={styles.stretchedLink}
        >
          {_t('Learn more')}
        </Button>
      </div>
    </HoverEffectWrapper>
  );
};

export default DegreeSpecCard;
